import {
  Alert,
  Button,
  Divider,
  ExclamationTriangleIcon,
  Flex,
  Segment,
  Skeleton,
  Text,
  Tooltip,
  TrashCanIcon,
} from "@fluentui/react-northstar";
import { dialog } from "@microsoft/teams-js";
import FormState, {
  FieldDescriptor,
  FieldDescriptors,
  FormData,
  RemoteError,
  ValidatorDictionary,
  arrayUtils,
} from "@shopify/react-form-state";
import { AGILE_LEARN_MORE_LINK, SurveyAuthoringPreviewCommand, SurveyLimits } from "@teams/cards";
import { createNominalQuestion, createOpenEndedQuestion } from "@teams/client-shared";
import { Question, QuestionType, TeamsSurveyDraft } from "@teams/types";
import moment from "moment";
import React from "react";

import { FieldLabel, TextInputField } from "../../../_shared/components/fields";
import { PollyIcon } from "../../../_shared/components/PollyIcon";
import { ReminderSelector } from "../../../_shared/components/ReminderSelector";
import { isError } from "../../../_shared/services/ChannelApi";
import { scheduleApi } from "../../../_shared/services/ScheduleApi";
import { useTeamsContext } from "../../../_shared/state/TeamsContextProvider";
import { getAudienceFromTeamsContext } from "../../../_shared/utils/Authoring";
import { getRRuleDTStart } from "../../../_shared/utils/scheduleHelper";
import {
  getQuestionValidator,
  getSurveyDescriptionValidator,
  getSurveyRemindersValidator,
  getSurveyTitleValidator,
  validateList,
} from "../../../_shared/validators";
import { AuthoringFormHeader } from "../../../survey/pages/SurveyAuthoring/AuthoringFormHeader";
import { OpenDurationField } from "../../../survey/pages/SurveyAuthoring/OpenDurationField";
import { PollyQuestionEdit } from "../../../survey/pages/SurveyAuthoring/PollyQuestionEdit";
import { PollyQuestionTypeMenu } from "../../../survey/pages/SurveyAuthoring/PollyQuestionTypeMenu";
import {
  AuthoringFields,
  AuthoringFormHeaderOptions,
} from "../../../survey/pages/SurveyAuthoring/PollySurveyAuthoringForm";
import { SchedulePollyField } from "../../../survey/pages/SurveyAuthoring/SchedulePollyField";

type Props = {
  editScheduleId?: string;
  externalErrors?: RemoteError[];
  surveyData: AuthoringFields;
  reminderSizeLimit: number;
  showLoading?: boolean;
  headerOptions?:
    | {
        showHeader?: false;
      }
    | ({
        showHeader: true;
      } & AuthoringFormHeaderOptions);
};

const MIN_QUESTIONS = 2;
const MAX_QUESTIONS = 5;

export const AgilePollyAuthoringForm: React.FC<Props> = props => {
  const {
    editScheduleId,
    externalErrors,
    surveyData,
    reminderSizeLimit,
    showLoading,
    headerOptions,
  } = props;

  const { teamsContext } = useTeamsContext();

  const audience = getAudienceFromTeamsContext(teamsContext)!;

  const createStandup = async (data: FormData<AuthoringFields>): Promise<void> => {
    const { fields } = data;

    const draft = formFieldsToDraft(fields);

    const audience = getAudienceFromTeamsContext(teamsContext)!;
    draft.recipients = [audience];

    const submitCommand = SurveyAuthoringPreviewCommand(draft, {});

    const cmd = submitCommand.toCommand();

    return new Promise(() => {
      dialog.url.submit(cmd);
    });
  };

  const editStandup = async (data: FormData<AuthoringFields>): Promise<void | [Error]> => {
    const { fields } = data;

    const draft = formFieldsToDraft(fields);

    const result = await scheduleApi.update(teamsContext, editScheduleId as string, draft);
    if (isError(result)) {
      return [result];
    }

    return new Promise(() => {
      dialog.url.submit();
    });

  };

  if (showLoading) {
    return (
      <Skeleton animation="wave" className="polly-authoring-page">
        <Flex column gap="gap.medium">
          <Skeleton.Line width="50%" />
          <Skeleton.Line width="50%" />
          <Skeleton.Line width="50%" />
          <Skeleton.Line height="2px" />
          <Skeleton.Shape width="100%" height="150px" />
          <Skeleton.Shape width="100%" height="150px" />
          <Skeleton.Shape width="100%" height="150px" />
          <Skeleton.Line height="2px" />
          <Skeleton.Button />
        </Flex>
      </Skeleton>
    );
  }

  return (
    <>
      {headerOptions?.showHeader && (
        <AuthoringFormHeader
          isEdit={headerOptions.isEdit}
          templateId={headerOptions.templateId}
          trackingSource={headerOptions.trackingSource}
        />
      )}

      <div>
        <FormState
          validateOnSubmit
          externalErrors={externalErrors}
          initialValues={surveyData}
          validators={getValidator()}
          onSubmit={formData => {
            if (!editScheduleId) {
              return createStandup(formData);
            } else {
              return editStandup(formData);
            }
          }}
        >
          {({ fields, submitting, submit, errors }) => {
            const onDeleteQuestion = (questionId: string) => {
              const toDeleteIndex = fields.questions.value.findIndex(q => q.id === questionId);
              if (toDeleteIndex !== -1) {
                const questions = fields.questions.value.filter((_q, i) => toDeleteIndex !== i);

                fields.questions.onChange(questions);
              }
            };

            const addQuestion = () => {
              if (fields.questions.value.length >= 5) {
                return;
              }
              const qs = arrayUtils.push(fields.questions.value, createOpenEndedQuestion());
              fields.questions.onChange(qs);
            };

            const onQuestionTypeChanged = (questionId: string, newType: QuestionType) => {
              const questionIndex = fields.questions.value.findIndex(q => q.id === questionId);
              const question = fields.questions.value[questionIndex];

              if (question && question.questionType !== newType) {
                const label = question.label;
                const id = question.id;
                let newq: Question<QuestionType>;
                switch (newType) {
                  case "open-ended":
                    newq = createOpenEndedQuestion({}, id);
                    break;
                  case "nominal":
                    newq = createNominalQuestion({}, id);
                    break;
                  default:
                    return;
                }
                newq.label = label;

                fields.questions.value[questionIndex] = newq;

                fields.questions.onChange([...fields.questions.value]);
              }
            };

            return (
              <>
                <form>
                  <Flex column gap="gap.medium">
                    {teamsContext?.user?.id === audience.id && (
                      <Alert
                        className="authoring__audience-display"
                        content="You are only sending this standup to yourself!  If you would like to send this to a team of people, add AgilePolly to a channel or groupChat to get started!"
                        warning
                        icon={<ExclamationTriangleIcon />}
                        actions={[
                          {
                            key: "learn-more",
                            content: "Learn more",
                            onClick: () => window.open(AGILE_LEARN_MORE_LINK, "_blank"),
                          },
                        ]}
                      />
                    )}

                    <Segment>
                      <Flex gap="gap.medium" column>
                        <Flex>
                          <Flex.Item size="size.half">
                            <div>
                              <TextInputField
                                maxLength={100}
                                minLength={SurveyLimits.Title.MinLength}
                                field={fields.title}
                                label="Team name"
                                placeholder="Enter the name of your team"
                                required
                                fluid
                                tooltip="Title of the team that this standup targets"
                              />
                            </div>
                          </Flex.Item>
                        </Flex>

                        <TextInputField
                          maxLength={SurveyLimits.Description.MaxLength}
                          field={fields.description as FieldDescriptor<string>}
                          label="Message to your team"
                          placeholder="Enter a message to your team"
                          fluid
                          tooltip="An appeal message you wish to add to standup"
                        />

                        <SchedulePollyField
                          gap="gap.medium"
                          column
                          sendAt={fields.sendAt as FieldDescriptor<Date>}
                          rrule={fields.rrule}
                        />

                        <div>
                          <OpenDurationField
                            field={fields.openDuration}
                            label="Close standup after"
                            allowManual={false}
                          />
                        </div>

                        <Flex column hAlign="start">
                          <FieldLabel
                            label="Reminders"
                            size="medium"
                            note={`* reminders are only sent to teams of ${reminderSizeLimit} members or less`}
                          />
                          <ReminderSelector field={fields.reminders} maxEntries={2} />
                        </Flex>
                      </Flex>
                    </Segment>

                    <div>
                      <span className="authoring__icon-header icon-survey-question-icon polly-accent-color"></span>
                      <Text size="large" weight="bold" content="Standup questions" />
                    </div>

                    <Flex column gap="gap.medium">
                      <FormState.List field={fields.questions} getChildKey={item => item.id}>
                        {(listFields, index) => {
                          return (
                            <Segment color="brand">
                              <Flex column gap="gap.medium">
                                <Flex gap="gap.small" vAlign="center">
                                  {fields.questions.value.length > 1 && (
                                    <Text content={`${index + 1}.`} weight="bold" />
                                  )}
                                  <Flex.Item grow>
                                    <div>
                                      <PollyQuestionTypeMenu
                                        activeType={listFields.questionType.value}
                                        templates={[]}
                                        allowedTypes={["nominal", "open-ended"]}
                                        onTypeSelected={type =>
                                          onQuestionTypeChanged(
                                            listFields.id.value,
                                            type as QuestionType
                                          )
                                        }
                                      />
                                    </div>
                                  </Flex.Item>
                                  <Flex.Item push>
                                    <div>
                                      {fields.questions.value.length > MIN_QUESTIONS && (
                                        <Flex>
                                          {fields.questions.value.length > 1 && (
                                            <Tooltip content="Delete question">
                                              <Button
                                                iconOnly
                                                text
                                                icon={<TrashCanIcon />}
                                                onClick={() =>
                                                  onDeleteQuestion(listFields.id.value)
                                                }
                                              />
                                            </Tooltip>
                                          )}
                                        </Flex>
                                      )}
                                    </div>
                                  </Flex.Item>
                                </Flex>
                                <PollyQuestionEdit
                                  field={listFields}
                                  templates={[]}
                                  onTemplateSelected={() => {}}
                                  questionSettings={{
                                    hideRequired: true,
                                    allowMultipleVotes: false,
                                    allowCorrectChoice: false,
                                    audienceCanAddChoice: false,
                                    allowedContentTypes: ["text/plain"],
                                    enableQuestionComments:
                                      listFields.questionType.value === "nominal",
                                  }}
                                />
                              </Flex>
                            </Segment>
                          );
                        }}
                      </FormState.List>
                    </Flex>

                    <Button
                      secondary
                      fluid
                      onClick={e => {
                        addQuestion();
                        e.preventDefault();
                      }}
                      disabled={fields.questions.value.length >= MAX_QUESTIONS}
                      content={
                        <PollyIcon icon="add-question-icon" ignoreTheme label="Add Question" />
                      }
                    />

                    {errors && errors.length > 0 && (
                      <>
                        {errors.map((error, index) => (
                          <Alert
                            key={index}
                            danger
                            content={`${error.field ? error.field + ": " : ""}${error.message}`}
                            icon={<ExclamationTriangleIcon />}
                          />
                        ))}
                      </>
                    )}

                    <Divider color="brand" />

                    <Flex hAlign="end" style={{ marginBottom: "1rem" }}>
                      <Button
                        primary
                        loading={submitting}
                        disabled={submitting === true}
                        content={editScheduleId ? "Save" : "Preview"}
                        onClick={e => {
                          submit();
                          e.preventDefault();
                        }}
                      />
                    </Flex>
                  </Flex>
                </form>
              </>
            );
          }}
        </FormState>
      </div>
    </>
  );
};

function formFieldsToDraft(fields: FieldDescriptors<AuthoringFields>) {
  const draft: TeamsSurveyDraft = {
    type: "agile-standup",
    title: fields.title.value,
    description: fields.description.value,
    commentsEnabled: false,
    resultsVisibility: "real-time",
    resultsCloseNotification: true,
    anonymity: "none",
    questions: fields.questions.value,
    reminders: fields.reminders.value.map(r => ({ duration: r.duration })),
    deliveryPreference: "in-group",
  };

  if (fields.rrule.value) {
    draft.schedule = {
      rrule: fields.rrule.value,
      rruleDTStart: getRRuleDTStart(fields.sendAt.value!),
      rruleTZID: Intl.DateTimeFormat().resolvedOptions().timeZone,
      openDuration: fields.openDuration.value as string,
    };
  } else {
    draft.deliverAt = moment(fields.sendAt.value).isAfter() ? fields.sendAt.value : new Date();
    draft.openDuration = fields.openDuration.value;
  }
  return draft;
}

function getValidator(): Partial<ValidatorDictionary<AuthoringFields>> {
  return {
    title: getSurveyTitleValidator("Team name", 100) as any,
    description: getSurveyDescriptionValidator,
    // tslint:disable-next-line: no-any
    reminders: validateList(getSurveyRemindersValidator() as any),
    sendAt: getSurveySendAtValidator("Send at") as any,
    // tslint:disable-next-line: no-any
    questions: validateList(getQuestionValidator() as any),
  };
}

export const getSurveySendAtValidator =
  (title: string) =>
  (input: Date | null): any => {
    if (!input) {
      return `${title} is required`;
    }

    const duration = moment.duration(SurveyLimits.DeliverAt.MaxDurationFromNow);
    const maxDate = moment().add(duration).toDate();
    if (moment(input).isAfter(maxDate)) {
      return `${title} must be no more than ${duration.humanize()} in the future`;
    }
  };
